import { useState, useEffect } from "react";
import { useHistory, NavLink } from "react-router-dom";
import firebase from "firebase/app";
import { db, auth } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import "../CSS/admin.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

const CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD!;
const PRESET = process.env.REACT_APP_CLOUDINARY_PRESET!;

async function uploadToCloudinary(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  form.append("upload_preset", PRESET);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD}/image/upload`, {
    method: "POST",
    body: form,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error?.message || "Image upload failed");
  return data.secure_url as string;
}

interface Entry {
  content: string;
  imageUrl?: string | null;
  imageUrls?: string[] | null;
  timestamp?: any;
}

// Legacy entries stored a single `imageUrl`; new ones use `imageUrls`.
const imagesOf = (entry: Entry): string[] =>
  entry.imageUrls ?? (entry.imageUrl ? [entry.imageUrl] : []);

interface Trip {
  id: string;
  title: string;
  location: string;
  date: string;
  imageUrl: string;
}

type Mode = "new" | "entry" | "edit";

function ImagePicker({
  label,
  onUrl,
  onUploadingChange,
}: {
  label: string;
  onUrl: (url: string | null) => void;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setErr("");
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const url = await uploadToCloudinary(file);
      onUrl(url);
    } catch (e: any) {
      console.error("Upload error:", e);
      setErr(e?.message || "Uppladdning misslyckades, försök igen.");
      onUrl(null);
    }
    setUploading(false);
    onUploadingChange?.(false);
  };

  return (
    <div className="admin__picker">
      <label className="admin__picker-label">
        {uploading ? "Laddar upp…" : label}
        <input
          type="file"
          accept="image/*"
          className="admin__picker-input"
          onChange={handleFile}
        />
      </label>
      {err && <p className="admin__error">{err}</p>}
      {preview && <img src={preview} className="admin__preview" alt="preview" />}
    </div>
  );
}

function MultiImagePicker({
  label,
  images,
  onChange,
  onUploadingChange,
}: {
  label: string;
  images: string[];
  onChange: (urls: string[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file(s)
    if (!files.length) return;
    setErr("");
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const urls = await Promise.all(files.map(uploadToCloudinary));
      onChange([...images, ...urls]);
    } catch (e: any) {
      console.error("Upload error:", e);
      setErr(e?.message || "Uppladdning misslyckades, försök igen.");
    }
    setUploading(false);
    onUploadingChange?.(false);
  };

  const removeAt = (i: number) => onChange(images.filter((_, idx) => idx !== i));

  return (
    <div className="admin__picker">
      <label className="admin__picker-label">
        {uploading ? "Laddar upp…" : label}
        <input
          type="file"
          accept="image/*"
          multiple
          className="admin__picker-input"
          onChange={handleFiles}
        />
      </label>
      {err && <p className="admin__error">{err}</p>}
      {images.length > 0 && (
        <div className="admin__thumbs">
          {images.map((url, i) => (
            <div key={i} className="admin__thumb">
              <img src={url} alt="" />
              <button
                type="button"
                className="admin__thumb-remove"
                onClick={() => removeAt(i)}
                aria-label="Ta bort foto"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const AdminView = () => {
  usePageBackground();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [selectedTripId, setSelectedTripId] = useState("");
  const [selectedTripEntries, setSelectedTripEntries] = useState<Entry[]>([]);
  const [mode, setMode] = useState<Mode>("new");

  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [content, setContent] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [entryImages, setEntryImages] = useState<string[]>([]);
  const [entryUploading, setEntryUploading] = useState(false);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editImages, setEditImages] = useState<string[]>([]);
  const [editUploading, setEditUploading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [pickerKey, setPickerKey] = useState(0);
  const history = useHistory();

  useEffect(() => {
    db.collection("trips")
      .orderBy("createdAt", "desc")
      .get()
      .then((snap) => {
        setTrips(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Trip, "id">) })));
      })
      .catch(() => {});
  }, [success]);

  const resetFields = () => {
    setTitle(""); setLocation(""); setDate(""); setContent(""); setEntryDate("");
    setCoverUrl(null); setCoverUploading(false); setEntryImages([]); setEntryUploading(false);
    setSelectedTripId(""); setSelectedTripEntries([]);
    setEditingIndex(null);
    setPickerKey((k) => k + 1);
  };

  // "2024-07-15" (local) from a Firestore Timestamp / Date, for <input type="date">.
  const toDateInput = (timestamp: any) => {
    if (!timestamp) return "";
    try {
      const d = timestamp.toDate?.() || new Date(timestamp);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
      return local.toISOString().slice(0, 10);
    } catch { return ""; }
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    resetFields();
    setError(""); setSuccess(false);
  };

  const handleTripSelect = async (id: string) => {
    setSelectedTripId(id);
    setSelectedTripEntries([]);
    if (!id) return;

    if (mode === "edit") {
      const snap = await db.collection("trips").doc(id).get();
      const data = snap.data() as any;
      if (data) {
        setTitle(data.title ?? "");
        setLocation(data.location ?? "");
        setDate(data.date ?? "");
        setCoverUrl(data.imageUrl ?? null);
        setSelectedTripEntries(data.entries ?? []);
      }
    }
  };

  const handleDeleteEntry = async (entry: Entry) => {
    if (!window.confirm("Ta bort detta inlägg?")) return;
    try {
      await db.collection("trips").doc(selectedTripId).update({
        entries: firebase.firestore.FieldValue.arrayRemove(entry),
      });
      setSelectedTripEntries((prev) => prev.filter((e) => e !== entry));
    } catch (err: any) {
      setError(err?.message || "Kunde inte ta bort inlägget.");
    }
  };

  const startEditEntry = (realIndex: number, entry: Entry) => {
    setEditingIndex(realIndex);
    setEditContent(entry.content);
    setEditDate(toDateInput(entry.timestamp));
    setEditImages(imagesOf(entry));
    setEditUploading(false);
    setError("");
  };

  const cancelEditEntry = () => {
    setEditingIndex(null);
    setEditContent(""); setEditDate(""); setEditImages([]); setEditUploading(false);
  };

  const handleSaveEntry = async (realIndex: number) => {
    const existing = selectedTripEntries[realIndex];
    const updated = [...selectedTripEntries];
    updated[realIndex] = {
      content: editContent,
      imageUrls: editImages,
      timestamp: editDate ? new Date(editDate) : (existing?.timestamp ?? new Date()),
    };
    try {
      await db.collection("trips").doc(selectedTripId).update({ entries: updated });
      setSelectedTripEntries(updated);
      cancelEditEntry();
    } catch (err: any) {
      setError(err?.message || "Kunde inte spara inlägget.");
    }
  };

  const handleDeleteTrip = async () => {
    const trip = trips.find((t) => t.id === selectedTripId);
    if (!window.confirm(`Ta bort hela resan "${trip?.title}"? Det går inte att ångra.`)) return;
    try {
      await db.collection("trips").doc(selectedTripId).delete();
      resetFields();
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || "Kunde inte ta bort resan.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess(false);

    const entryTimestamp = entryDate ? new Date(entryDate) : new Date();

    try {
      if (mode === "new") {
        await db.collection("trips").add({
          title, location, date,
          // The cover is optional: fall back to the first photo in the post
          imageUrl: coverUrl ?? entryImages[0] ?? null,
          entries: [{ content, imageUrls: entryImages, timestamp: entryTimestamp }],
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      } else if (mode === "entry" && selectedTripId) {
        await db.collection("trips").doc(selectedTripId).update({
          entries: firebase.firestore.FieldValue.arrayUnion({
            content,
            imageUrls: entryImages,
            timestamp: entryTimestamp,
          }),
        });
      } else if (mode === "edit" && selectedTripId) {
        const update: any = { title, location, date };
        if (coverUrl) update.imageUrl = coverUrl;
        await db.collection("trips").doc(selectedTripId).update(update);
      }

      resetFields();
      setSuccess(true);
    } catch (err: any) {
      console.error("Firestore error:", err);
      setError(err?.message || "Sparandet misslyckades.");
    }
    setSaving(false);
  };

  const formatEntryDate = (timestamp: any) => {
    if (!timestamp) return null;
    try {
      const d = timestamp.toDate?.() || new Date(timestamp);
      return d.toLocaleDateString();
    } catch { return null; }
  };

  const submitDisabled =
    saving ||
    coverUploading ||
    ((mode === "new" || mode === "entry") && entryUploading) ||
    ((mode === "entry" || mode === "edit") && !selectedTripId);

  return (
    <div className="admin">
      <div className="admin__header">
        <NavLink to={routeUrls.BLOGG} className="admin__back">← Resor</NavLink>
        <h1 className="admin__title">
          {mode === "new" ? "Ny resa" : mode === "entry" ? "Lägg till inlägg" : "Redigera resa"}
        </h1>
      </div>

      <div className="admin__mode-toggle">
        <button
          className={`admin__mode-btn${mode === "new" ? " admin__mode-btn--active" : ""}`}
          onClick={() => switchMode("new")}
        >
          Ny resa
        </button>
        <button
          className={`admin__mode-btn${mode === "entry" ? " admin__mode-btn--active" : ""}`}
          onClick={() => switchMode("entry")}
        >
          Lägg till inlägg
        </button>
        <button
          className={`admin__mode-btn${mode === "edit" ? " admin__mode-btn--active" : ""}`}
          onClick={() => switchMode("edit")}
        >
          Redigera resa
        </button>
      </div>

      <form className="admin__form" onSubmit={handleSubmit}>
        {(mode === "entry" || mode === "edit") && (
          <select
            className="admin__select"
            value={selectedTripId}
            onChange={(e) => handleTripSelect(e.target.value)}
            required
          >
            <option value="">Välj en resa</option>
            {trips.map((trip) => (
              <option key={trip.id} value={trip.id}>
                {trip.title} — {trip.location}
              </option>
            ))}
          </select>
        )}

        {(mode === "new" || mode === "edit") && (
          <>
            <input
              className="admin__input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Resetitel"
              required
            />
            <div className="admin__row">
              <input
                className="admin__input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Land / Region"
                required
              />
              <input
                className="admin__input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="t.ex. Jul 2024"
                required
              />
            </div>
            <ImagePicker
              key={`cover-${pickerKey}`}
              label={mode === "new" ? "Ladda upp omslagsfoto (valfritt)" : "Byt omslagsfoto"}
              onUrl={setCoverUrl}
              onUploadingChange={setCoverUploading}
            />
            {mode === "edit" && coverUrl && !coverUrl.startsWith("blob:") && (
              <img src={coverUrl} className="admin__preview" alt="current cover" />
            )}
          </>
        )}

        {(mode === "new" || mode === "entry") && (
          <>
            <label className="admin__field-label">
              Datum för inlägg
              <input
                type="date"
                className="admin__input"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
              />
            </label>
            <textarea
              className="admin__textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Skriv ditt inlägg här…"
              required
            />
            <MultiImagePicker
              label="Ladda upp foton (valfritt)"
              images={entryImages}
              onChange={setEntryImages}
              onUploadingChange={setEntryUploading}
            />
          </>
        )}

        {mode === "edit" && selectedTripId && selectedTripEntries.length > 0 && (
          <div className="admin__entries">
            <p className="admin__entries-label">Inlägg</p>
            {selectedTripEntries
              .map((entry, idx) => ({ entry, idx }))
              .reverse()
              .map(({ entry, idx }) => {
                if (editingIndex === idx) {
                  return (
                    <div key={idx} className="admin__entry-edit">
                      <label className="admin__field-label">
                        Datum för inlägg
                        <input
                          type="date"
                          className="admin__input"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                        />
                      </label>
                      <textarea
                        className="admin__textarea"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        required
                      />
                      <MultiImagePicker
                        label="Lägg till foton (valfritt)"
                        images={editImages}
                        onChange={setEditImages}
                        onUploadingChange={setEditUploading}
                      />
                      <div className="admin__entry-edit-actions">
                        <button
                          type="button"
                          className="admin__entry-save"
                          onClick={() => handleSaveEntry(idx)}
                          disabled={editUploading}
                        >
                          {editUploading ? "Laddar upp…" : "Spara inlägg"}
                        </button>
                        <button
                          type="button"
                          className="admin__entry-cancel"
                          onClick={cancelEditEntry}
                        >
                          Avbryt
                        </button>
                      </div>
                    </div>
                  );
                }
                const dateStr = formatEntryDate(entry.timestamp);
                return (
                  <div key={idx} className="admin__entry">
                    <div className="admin__entry-meta">
                      {dateStr && <span className="admin__entry-date">{dateStr}</span>}
                      <p className="admin__entry-preview">
                        {entry.content.slice(0, 80)}{entry.content.length > 80 ? "…" : ""}
                      </p>
                    </div>
                    <div className="admin__entry-actions">
                      <button
                        type="button"
                        className="admin__entry-edit-btn"
                        onClick={() => startEditEntry(idx, entry)}
                      >
                        Ändra
                      </button>
                      <button
                        type="button"
                        className="admin__entry-delete"
                        onClick={() => handleDeleteEntry(entry)}
                      >
                        Ta bort
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {error && <p className="admin__error">{error}</p>}
        {success && (
          <p className="admin__success">
            {mode === "new" ? "Resa publicerad!" : mode === "entry" ? "Inlägg tillagt!" : "Resa uppdaterad!"}
          </p>
        )}

        <button className="admin__submit" type="submit" disabled={submitDisabled}>
          {saving ? "Sparar…" : mode === "new" ? "Publicera resa" : mode === "entry" ? "Lägg till inlägg" : "Spara ändringar"}
        </button>

        {mode === "edit" && selectedTripId && (
          <button
            type="button"
            className="admin__delete-trip"
            onClick={handleDeleteTrip}
          >
            Ta bort hela resan
          </button>
        )}
      </form>
    </div>
  );
};
